import { useState, useEffect, useCallback } from "react";
import { Wifi, Database, Zap, Cpu, MapPin, Network, ChevronDown } from "lucide-react";
import wirelessImg from "@/assets/depin-wireless-new.jpg";
import computingImg from "@/assets/depin-computing-new.jpg";
import energyImg from "@/assets/depin-energy-new.jpg";
import aiImg from "@/assets/depin-ai-new.jpg";
import geospatialImg from "@/assets/depin-geospatial-new.jpg";
import iotImg from "@/assets/depin-iot-new.jpg";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";

const depinSectors = [
  {
    icon: Wifi,
    title: "Wireless Networks",
    description:
      "Decentralized connectivity infrastructure providing global coverage through community-owned nodes and hotspots.",
    longDescription:
      "Build and participate in community-powered wireless networks that provide affordable, censorship-resistant connectivity. Earn rewards for sharing bandwidth and maintaining network infrastructure.",
    image: wirelessImg,
    stats: {
      nodes: "50K+",
      coverage: "120 Countries",
      uptime: "99.9%",
    },
  },
  {
    icon: Database,
    title: "Computing & Storage",
    description: "Distributed cloud computing and storage solutions leveraging unused resources across the network.",
    longDescription:
      "Contribute idle computing power and storage capacity to decentralized networks. Power applications, store data securely, and earn passive income from your hardware.",
    image: computingImg,
    stats: {
      nodes: "25K+",
      storage: "50 PB",
      providers: "10K+",
    },
  },
  {
    icon: Zap,
    title: "Energy & Power",
    description: "Peer-to-peer energy networks enabling renewable energy trading and grid optimization.",
    longDescription:
      "Join the energy revolution with peer-to-peer renewable energy trading. Install solar panels, battery storage, or EV chargers and trade surplus energy directly with your community.",
    image: energyImg,
    stats: {
      nodes: "15K+",
      energy: "500 GWh",
      savings: "40%",
    },
  },
  {
    icon: Cpu,
    title: "AI/ML Infrastructure",
    description: "Decentralized GPU computing power for training and running AI models at scale.",
    longDescription:
      "Access affordable GPU compute power for AI model training and inference. Contribute your gaming PC or mining rig to earn rewards while powering the next generation of AI applications.",
    image: aiImg,
    stats: {
      gpus: "100K+",
      models: "5K+",
      cost: "-80%",
    },
  },
  {
    icon: MapPin,
    title: "Geospatial Mapping",
    description: "Community-sourced mapping data and location services built on blockchain technology.",
    longDescription:
      "Create and verify location data, build detailed maps, and provide positioning services. Earn rewards for contributing accurate geospatial information to the decentralized mapping network.",
    image: geospatialImg,
    stats: {
      mappers: "30K+",
      locations: "5M+",
      accuracy: "99.5%",
    },
  },
  {
    icon: Network,
    title: "Sensors & IoT",
    description: "Connected device networks collecting and sharing real-world data through decentralized protocols.",
    longDescription:
      "Deploy environmental sensors, weather stations, and IoT devices to collect valuable real-world data. Monetize your data contributions while helping build a global sensor network.",
    image: iotImg,
    stats: {
      sensors: "200K+",
      dataPoints: "1B+",
      types: "50+",
    },
  },
];

const CaseStudies = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const { ref: sectionRef, isVisible } = useScrollAnimation({
    threshold: 0.1,
  });

  const nextSector = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % depinSectors.length);
    setProgress(0);
  }, []);

  // Progress bar animation
  useEffect(() => {
    if (isPaused) return;
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) return 0;
        return prev + 2;
      });
    }, 100);
    return () => clearInterval(progressInterval);
  }, [isPaused, activeIndex]);

  // Auto-rotation
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(nextSector, 5000);
    return () => clearInterval(interval);
  }, [isPaused, nextSector]);

  const handleCategoryClick = (index: number) => {
    setActiveIndex(index);
    setProgress(0);
  };

  const activeSector = depinSectors[activeIndex];

  return (
    <section
      ref={sectionRef as React.RefObject<HTMLElement>}
      id="case-studies"
      className="py-12 md:py-16 relative overflow-hidden bg-background"
    >
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Header - zoom in effect */}
        <div
          className={`text-center max-w-3xl mx-auto mb-12 transition-all duration-1000 ease-out ${isVisible ? "scale-100 opacity-100 blur-0" : "scale-95 opacity-0 blur-sm"}`}
        >
          <p className="text-sm font-mono tracking-widest text-muted-foreground uppercase mb-4">
            Explore DePIN Sectors
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            The Future of Infrastructure
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Explore the ecosystem reshaping global infrastructure across multiple sectors.
          </p>
        </div>

        {/* Split Layout - slide in from right */}
        <div
          className={`grid lg:grid-cols-[380px_1fr] gap-4 lg:gap-8 transition-all duration-1000 ease-out delay-300 ${isVisible ? "translate-x-0 opacity-100 blur-0" : "translate-x-12 opacity-0 blur-sm"}`}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Left Side - Category List */}
          <div className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-x-visible pb-2 lg:pb-0 scrollbar-hide">
            {depinSectors.map((sector, index) => {
              const Icon = sector.icon;
              const isActive = index === activeIndex;
              return (
                <button
                  key={index}
                  onClick={() => handleCategoryClick(index)}
                  className={`group flex-shrink-0 lg:w-full relative flex flex-col rounded-2xl text-left transition-all duration-300 overflow-hidden
                    ${isActive ? "bg-card border border-border" : "bg-card/50 border border-border/50 hover:bg-muted/30 hover:border-border"}`}
                >
                  <div className="flex items-center gap-3 p-3 lg:p-4">
                    {/* Icon */}
                    <div
                      className={`p-3 rounded-xl transition-all duration-300
                      ${isActive ? "bg-muted/50" : "bg-muted/30 group-hover:bg-muted/50"}`}
                    >
                      <Icon
                        className={`w-5 h-5 lg:w-6 lg:h-6 transition-colors duration-300 ${isActive ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"}`}
                      />
                    </div>

                    {/* Text - Show title only on mobile, full on desktop */}
                    <div className="flex-1 min-w-0 hidden lg:block">
                      <p
                        className={`font-semibold transition-colors duration-300 ${isActive ? "text-foreground" : "text-foreground/80"}`}
                      >
                        {sector.title}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">{sector.description.slice(0, 50)}...</p>
                    </div>

                    {/* Mobile title */}
                    <span className={`lg:hidden text-xs font-medium whitespace-nowrap ${isActive ? "text-foreground" : "text-muted-foreground"}`}>
                      {sector.title.split(" ")[0]}
                    </span>

                    {/* Arrow - hidden on mobile */}
                    <ChevronDown
                      className={`hidden lg:block w-5 h-5 transition-all duration-300 -rotate-90
                      ${isActive ? "text-foreground translate-x-0 opacity-100" : "text-muted-foreground -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100"}`}
                    />
                  </div>

                  {/* Progress bar */}
                  <div
                    className={`h-0.5 bg-muted/30 transition-opacity duration-300 ${isActive ? "opacity-100" : "opacity-0"}`}
                  >
                    <div
                      className="h-full bg-foreground/50 transition-all duration-100 ease-linear"
                      style={{
                        width: isActive ? `${progress}%` : "0%",
                      }}
                    />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Right Side - Detail Display */}
          <div className="relative min-h-[400px] lg:min-h-[600px]">
            <div className="relative h-full rounded-2xl border border-border bg-card overflow-hidden">
              {/* Background Image with smooth transition */}
              <div className="absolute inset-0">
                {depinSectors.map((sector, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 bg-cover bg-center transition-all duration-700 ease-out
                      ${index === activeIndex ? "opacity-100 scale-100" : "opacity-0 scale-105"}`}
                    style={{
                      backgroundImage: `url(${sector.image})`,
                    }}
                  />
                ))}
              </div>

              {/* Gradient overlays */}
              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 to-background/40" />
              <div className="absolute inset-0 bg-gradient-to-r from-background/60 to-transparent" />

              {/* Content with smooth transitions */}
              <div className="relative h-full p-6 lg:p-12 flex flex-col justify-end">
                {depinSectors.map((sector, index) => {
                  const Icon = sector.icon;
                  const isActive = index === activeIndex;
                  return (
                    <div
                      key={index}
                      className={`absolute inset-0 p-6 lg:p-12 flex flex-col justify-end transition-all duration-700 ease-out
                        ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8 pointer-events-none"}`}
                    >
                      {/* Icon badge */}
                      <div
                        className={`p-3 rounded-xl bg-muted/50 w-fit mb-4 lg:mb-6 backdrop-blur-sm
                        transition-all duration-700 delay-100 ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                      >
                        <Icon className="w-6 h-6 lg:w-8 lg:h-8 text-foreground" />
                      </div>

                      {/* Title */}
                      <h3
                        className={`text-2xl lg:text-4xl font-bold text-foreground mb-3 lg:mb-4 transition-all duration-700 delay-150
                        ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                      >
                        {sector.title}
                      </h3>

                      {/* Description */}
                      <p
                        className={`text-base lg:text-lg text-muted-foreground leading-relaxed max-w-xl transition-all duration-700 delay-200
                        ${isActive ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
                      >
                        {sector.longDescription}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CaseStudies;
